<template>
  <div class="payment-status-table">
    <!-- Header Actions (CSV Import, etc.) -->
    <div v-if="showHeaderActions" class="table-header-actions">
      <slot name="header-actions">
        <Button v-if="showCsvImport" :label="t('Importar CSV')" icon="pi pi-upload" severity="secondary" outlined
          data-testid="payment-status-import-csv" @click="$emit('import-csv')" />
      </slot>
    </div>

    <!-- Bulk Action Toolbar -->
    <div v-if="selectedCompromissos.length > 0" class="bulk-action-toolbar">
      <div class="bulk-selection-info">
        <i class="pi pi-check-square"></i>
        <span>{{ selectedCompromissos.length }} {{ t('selecionado(s)') }}</span>
        <span class="bulk-selection-amount">
          ({{ n(parseFloat(String(selectedTotalValor)) || 0, 'currency') }} {{ t('restante') }})
        </span>
      </div>
      <div class="bulk-actions">
        <Button :label="t('Limpar Seleção')" icon="pi pi-times" severity="secondary" outlined size="small"
          data-testid="payment-status-clear-selection" @click="clearSelection" />
        <Button :label="t('Criar Recebimentos')" icon="pi pi-plus" severity="success" size="small" :loading="isCreating"
          data-testid="payment-status-create-recebimentos" @click="$emit('create-recebimentos', selectedCompromissos)" />
      </div>
    </div>

    <!-- Data Table -->
    <DataTable ref="dataTableRef" :key="tableKey" display-mode="handle_user_device" :config="tableConfigComputed"
      :data="enrichedCompromissos" :auto-load="false"
      :refreshButton="showRefreshButton ? { show: true } : { show: false }" v-model:selection="selectedCompromissos"
      @refresh="$emit('refresh')" @edit-click="handleEditClick" @button-click="handleButtonClick"
      @selection-change="handleSelectionChange" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DataTable from '../../grid/DataTable.vue';
import { log } from '../../utils/DebugLogger';
import { config } from '../../services/configService';
// Feature `baas` (backend registry, skill feature-flags).
const baasEnabled = config.isFeatureEnabled('baas');

import { permissionsService } from '../../services/permissionsService';

interface Compromisso {
  [key: string]: any;
}

const props = defineProps<{
  /** Array of compromissos with payment status data */
  compromissos?: Compromisso[];
  /** Loading state for bulk creation */
  isCreating?: boolean;
  /** Show CSV import button in header */
  showCsvImport?: boolean;
  /** Show header actions section */
  showHeaderActions?: boolean;
  /** Show refresh button */
  showRefreshButton?: boolean;
  /** Compact mode for smaller spaces (wizard steps) */
  compact?: boolean;
  /** Altura fixa do corpo da tabela, em px ('640px'). Ausente = a tabela mede a janela. */
  tableHeight?: string;
  /** Page size for pagination */
  pageSize?: number;
  /** Hide rows that are already fully conciliated (total == faturado) */
  hideConciliated?: boolean;
  /**
   * Whether the selected franchise has a subaccount configured
   * @deprecated Use franchisesWithSubaccounts for per-row visibility
   */
  hasSubaccount?: boolean;
  /** Array of franchise IDs that have active subaccounts configured */
  franchisesWithSubaccounts?: number[];
}>();

const emit = defineEmits<{
  (e: 'refresh'): void;
  (e: 'import-csv'): void;
  (e: 'create-recebimentos', items: Compromisso[]): void;
  (e: 'view-compromisso', id: any): void;
  (e: 'view-cliente', id: any): void;
  (e: 'open-recebimento-wizard', payload: { compromissoId: any; franquiaId: any }): void;
  (e: 'create-payment-link', payload: any): void;
  (e: 'selection-change', payload: { selection: Compromisso[]; added: Compromisso[]; removed: Compromisso[] }): void;
}>();

const { t, n } = useI18n();

// Refs
const dataTableRef = ref(null);
const tableKey = ref(0);
const selectedCompromissos = ref([]);

// Computed: Total valor restante for selected compromissos
const selectedTotalValor = computed(() => {
  return selectedCompromissos.value.reduce((sum: any, c: any) => {
    return sum + (c.valor_restante || 0);
  }, 0);
});

// Computed: Enriched compromissos data with normalized status fields for filtering
const enrichedCompromissos = computed(() => {
  let data = props.compromissos;

  // Filter out already conciliated rows if hideConciliated is enabled
  // A row is conciliated when total (compromisso_valor_total) equals faturado (valor_pago)
  if (props.hideConciliated) {
    data = (data as any[]).filter(c => {
      const total = parseFloat(c.compromisso_valor_total) || 0;
      const faturado = parseFloat(c.valor_pago) || 0;
      // Keep rows where there is still a difference (not fully conciliated)
      // Using small epsilon for float comparison
      const isFullyConciliated = Math.abs(total - faturado) < 0.01 && total > 0;
      return !isFullyConciliated;
    });
    log('PaymentStatusTable', 'enrichedCompromissos', 'Filtered conciliated rows', {
      original: props.compromissos!.length,
      filtered: (data as any[]).length
    });
  }

  return (data as any[]).map(c => {
    // Get franchise ID from compromisso data
    const franquiaId = c.compromisso_franquia_id || c.franquia_id;

    // Get franchise name - try from data first, then from permissions service
    let franquiaNome = c.franquia_nome || c.compromisso_franquia_nome || '';
    if (!franquiaNome && franquiaId) {
      franquiaNome = permissionsService.getFranchiseName(franquiaId);
    }

    return {
      ...c,
      // Normalize compromisso_status for filtering - use the same logic as renderCompromissoStatus
      compromisso_status: c.compromisso_status || c.status || t('Desconhecido'),
      // Normalize status_financeiro for filtering - use the same logic as renderStatusFinanceiro
      status_financeiro: c.status_financeiro || t('Desconhecido'),
      // Enrich franquia_nome for display and filtering
      franquia_nome: franquiaNome || t('Desconhecido')
    };
  });
});

// Computed: Unique compromisso status options for filter dropdown
const compromissoStatusFilterOptions = computed(() => {
  const uniqueStatuses = new Set();
  enrichedCompromissos.value.forEach(c => {
    if (c.compromisso_status) {
      uniqueStatuses.add(c.compromisso_status);
    }
  });
  return Array.from(uniqueStatuses)
    .sort((a: any, b: any) => a.localeCompare(b))
    .map((status: any) => ({
      label: status,
      value: status
    }));
});

// Computed: Unique payment status options for filter dropdown
const statusFinanceiroFilterOptions = computed(() => {
  const uniqueStatuses = new Set();
  enrichedCompromissos.value.forEach(c => {
    if (c.status_financeiro) {
      uniqueStatuses.add(c.status_financeiro);
    }
  });
  return Array.from(uniqueStatuses)
    .sort((a: any, b: any) => a.localeCompare(b))
    .map((status: any) => ({
      label: status,
      value: status
    }));
});

// Computed: Unique payment method options for filter dropdown
const formaPagamentoFilterOptions = computed(() => {
  const uniqueFormas = new Set();
  enrichedCompromissos.value.forEach(c => {
    if ((c as any).forma_pagamento_nome) {
      uniqueFormas.add((c as any).forma_pagamento_nome);
    }
  });
  return Array.from(uniqueFormas)
    .sort((a: any, b: any) => a.localeCompare(b))
    .map((forma: any) => ({
      label: forma,
      value: forma
    }));
});

// Computed: Unique operador options for filter dropdown
const operadorFilterOptions = computed(() => {
  const uniqueOperadores = new Set();
  enrichedCompromissos.value.forEach(c => {
    if ((c as any).operador) {
      uniqueOperadores.add((c as any).operador);
    }
  });
  return Array.from(uniqueOperadores)
    .sort((a: any, b: any) => a.localeCompare(b))
    .map((operador: any) => ({
      label: operador,
      value: operador
    }));
});

// Computed: Unique franquia options for filter dropdown
const franquiaFilterOptions = computed(() => {
  const uniqueFranquias = new Set();
  enrichedCompromissos.value.forEach(c => {
    if (c.franquia_nome) {
      uniqueFranquias.add(c.franquia_nome);
    }
  });
  return Array.from(uniqueFranquias)
    .sort((a: any, b: any) => a.localeCompare(b))
    .map((franquia: any) => ({
      label: franquia,
      value: franquia
    }));
});

// Render functions
const renderStatusFinanceiro = ({ value, data }: any) => {
  const severityMap = {
    0: 'danger',
    1: 'warning',
    2: 'success'
  };
  const severity = (severityMap as any)[data.compromisso_status_financeiro] || 'danger';
  const label = value || 'Desconhecido';
  return `<span class="p-tag p-tag-${severity}">${label}</span>`;
};

const renderCompromissoStatus = ({ value, data }: any) => {
  // Status mapping for compromisso status (appointment status, not payment)
  const statusId = data.compromisso_status_id || data.status_id;
  const statusName = value || data.compromisso_status || data.status || '';

  // Map common status IDs to severities (adjust based on your status definitions)
  // Common statuses: 1=Ativo, 2=Cancelado, 3=Concluído, 4=Reagendado, etc.
  const severityMap = {
    '1': 'info',      // Ativo
    '2': 'danger',    // Cancelado
    '3': 'success',   // Concluído
    '4': 'warning',   // Reagendado
    '5': 'secondary'  // Outro
  };
  const severity = (severityMap as any)[String(statusId)] || 'secondary';
  const label = statusName || t('Desconhecido');
  return `<span class="p-tag p-tag-${severity}">${label}</span>`;
};

// Renders Total / Faturado / Líquido column
// Total = compromisso_valor_total (what should be paid)
// Faturado = valor_pago (gross amount received)
// Líquido = valor_liquido (net amount after fees)
const renderValorRecebido = ({ value, data }: any) => {
  const valorTotal = data.compromisso_valor_total || 0;
  const valorFaturado = data.valor_pago || 0;
  const valorLiquido = data.valor_liquido || 0;
  return `<span class="valores-column">
    <span class="valor-total">${n(parseFloat(valorTotal) || 0, 'currency')}</span>
    <span class="valor-separator">/</span>
    <span class="valor-faturado">${n(parseFloat(valorFaturado) || 0, 'currency')}</span>
    <span class="valor-separator">/</span>
    <span class="valor-liquido">${n(parseFloat(valorLiquido) || 0, 'currency')}</span>
  </span>`;
};

// Footer renderer for the "Total / Faturado / Líquido" column - displays sum of all values
const renderValorRecebidoFooter = ({ data }: any) => {
  const valorTotalSum = data.compromisso_valor_total || 0;
  const valorFaturadoSum = data.valor_pago || 0;
  const valorLiquidoSum = data.valor_liquido || 0;
  return `<span class="valores-column">
    <span class="valor-total">${n(parseFloat(valorTotalSum) || 0, 'currency')}</span>
    <span class="valor-separator">/</span>
    <span class="valor-faturado">${n(parseFloat(valorFaturadoSum) || 0, 'currency')}</span>
    <span class="valor-separator">/</span>
    <span class="valor-liquido">${n(parseFloat(valorLiquidoSum) || 0, 'currency')}</span>
  </span>`;
};

const renderFormaPagamento = ({ value, data }: any) => {
  const formaPagamento = value || data.forma_pagamento_nome || '';
  if (!formaPagamento) {
    return `<span class="text-muted">-</span>`;
  }
  return `<span>${formaPagamento}</span>`;
};

const renderWhatsAppLink = ({ value, data }: any) => {
  const telefone = value || data.cliente_telefone;
  const ddd = data.cliente_ddd;
  const ddi = data.cliente_ddi;

  if (!telefone || !ddd || !ddi) {
    return `<span class="text-muted">-</span>`;
  }

  // Format phone numbers (remove non-digits)
  const telefoneFormatted = String(telefone).replace(/\D/g, '');
  const ddiFormatted = String(ddi).replace(/\D/g, '');
  const dddFormatted = String(ddd).replace(/\D/g, '');

  const whatsappUrl = `https://api.whatsapp.com/send?phone=${ddiFormatted}${dddFormatted}${telefoneFormatted}`;
  const displayNumber = `${ddiFormatted}${ddd}${telefone}`;

  return `<a class="p-button p-button-success p-button-sm btn-whatsapp" href="${whatsappUrl}" target="_blank" title="WhatsApp"><i class="pi pi-whatsapp"></i> ${displayNumber}</a>`;
};

// Row class function - mark "Pago" rows as not selectable
const getRowClass = (data: any) => {
  return data.compromisso_status_financeiro === "2" ? 'row-not-selectable' : '';
};

// Table configuration
const tableConfigComputed = computed(() => ({
  columns: [
    {
      data: 'compromisso_id',
      title: t('ID'),
      renderType: 'clickable_id',
      minWidth: 92,
      sortable: true
    },
    {
      data: 'compromisso_data',
      title: t('Data'),
      renderType: 'date',
      minWidth: 115,
      sortable: true
    },
    {
      data: 'franquia_nome',
      title: t('Franquia'),
      minWidth: 100,
      sortable: true,
      filterType: 'select',
      filterOptions: franquiaFilterOptions.value
    },
    {
      data: 'compromisso_status',
      title: t('Status Compromisso'),
      minWidth: 110,
      sortable: true,
      filterType: 'select',
      filterOptions: compromissoStatusFilterOptions.value,
      cellRenderer: renderCompromissoStatus
    },
    {
      data: 'status_financeiro',
      title: t('Status Pagamento'),
      minWidth: 110,
      sortable: true,
      filterType: 'select',
      filterOptions: statusFinanceiroFilterOptions.value,
      cellRenderer: renderStatusFinanceiro
    },
    {
      data: 'forma_pagamento_nome',
      title: t('Forma de Pagamento'),
      minWidth: 118,
      sortable: true,
      filterType: 'select',
      filterOptions: formaPagamentoFilterOptions.value,
      cellRenderer: renderFormaPagamento
    },
    {
      data: 'operador',
      title: t('Operador'),
      minWidth: 110,
      sortable: true,
      filterType: 'select',
      filterOptions: operadorFilterOptions.value
    },
    {
      data: 'cliente_nome',
      title: t('Cliente'),
      renderType: 'custom_clickable_id',
      minWidth: 150,
      sortable: true,
      clickCallback: handleClienteClick
    },
    {
      data: 'cliente_telefone',
      title: 'WhatsApp',
      minWidth: 130,
      sortable: true,
      cellRenderer: renderWhatsAppLink
    },
    {
      data: 'valor_pago',
      title: t('Total / Faturado / Líquido'),
      minWidth: 150,
      sortable: true,
      cellRenderer: renderValorRecebido,
      footerType: 'custom',
      footerSumFields: ['compromisso_valor_total', 'valor_pago', 'valor_liquido'],
      footerRenderer: renderValorRecebidoFooter
    },
    {
      data: null,
      title: t('Ações'),
      renderType: 'action_buttons',
      minWidth: 150,
      sortable: false,
      buttons: [
        {
          text: t('Recebimento'),
          icon: 'pi pi-plus',
          class: 'p-button-success p-button-sm',
          action: 'open-wizard'
        },
        {
          text: t('Link'),
          icon: 'pi pi-link',
          class: 'p-button-info p-button-sm',
          action: 'create-payment-link',
          tooltip: t('Criar link de pagamento'),
          // Per-row visibility: only show if franchise has a subaccount configured
          condition: (rowData: any) => {
            if (!baasEnabled) return false;
            const franquiaId = rowData.compromisso_franquia_id || rowData.franquia_id;
            // If franchisesWithSubaccounts is provided, use per-row check
            // Otherwise, fall back to hasSubaccount prop for backwards compatibility
            if (props.franchisesWithSubaccounts!.length > 0) {
              return props.franchisesWithSubaccounts!.includes(Number(franquiaId));
            }
            return props.hasSubaccount;
          }
        }
      ]
    }
  ],
  sortable: true,
  pagination: true,
  pageLength: props.pageSize ?? 100,
  // Sem `tableHeight` a tabela mede a janela (o padrão): vai até o fim dela e nunca fica com menos
  // de 50% da altura da janela, mesmo abaixo da dobra (chamado 2026-0078). `tableHeight` fica para
  // quem precisa de uma altura fixa.
  ...(props.tableHeight ? { heightMode: 'fixed', height: props.tableHeight } : { heightMode: 'fill-viewport' }),
  showFilters: true,
  stripedRows: true,
  showGridlines: true,
  rowClass: getRowClass,
  checkbox: {
    enabled: true,
    position: 'first',
    frozen: false,
    width: '3rem'
  }
}));

// Watch: Filter out "Pago" rows from selection (safety net)
watch(selectedCompromissos, (newVal) => {
  const filtered = newVal.filter((c: any) => (c as any).compromisso_status_financeiro !== "2");
  if (filtered.length !== newVal.length) {
    log('PaymentStatusTable', 'watch:selectedCompromissos', 'Filtered out Pago rows', {
      before: newVal.length,
      after: filtered.length
    });
    selectedCompromissos.value = filtered;
  }
}, { deep: true });

// Methods
const handleEditClick = (compromissoId: any) => {
  log('PaymentStatusTable', 'handleEditClick', 'Opening compromisso', compromissoId);
  emit('view-compromisso', compromissoId);
};

const handleClienteClick = (clienteNome: any, rowData: any) => {
  log('PaymentStatusTable', 'handleClienteClick', 'Opening cliente', rowData.cliente_id);
  emit('view-cliente', rowData.cliente_id);
};

const handleButtonClick = ({ data, action }: any) => {
  log('PaymentStatusTable', 'handleButtonClick', 'Button clicked', { data, action });
  if (action === 'open-wizard') {
    emit('open-recebimento-wizard', {
      compromissoId: data.compromisso_id,
      franquiaId: data.compromisso_franquia_id || data.franquia_id
    });
  } else if (action === 'create-payment-link') {
    emit('create-payment-link', {
      compromissoId: data.compromisso_id,
      franquiaId: data.compromisso_franquia_id || data.franquia_id,
      clienteNome: data.cliente_nome,
      valorRestante: data.valor_restante || 0,
      compromissoData: data
    });
  }
};

const handleSelectionChange = ({ selection, added, removed }: any) => {
  log('PaymentStatusTable', 'handleSelectionChange', 'Selection changed', {
    total: selection.length,
    added: added.length,
    removed: removed.length
  });
  emit('selection-change', { selection, added, removed });
};

const clearSelection = () => {
  log('PaymentStatusTable', 'clearSelection', 'Clearing selection');
  selectedCompromissos.value = [];
  if (dataTableRef.value) {
    (dataTableRef.value as any).clearSelection();
  }
};

// Expose methods for parent components
const forceRefresh = () => {
  tableKey.value += 1;
};

const getSelectedCompromissos = () => {
  return selectedCompromissos.value;
};

defineExpose({
  clearSelection,
  forceRefresh,
  getSelectedCompromissos
});
</script>

<style scoped>
.payment-status-table {
  width: 100%;
}

/*
 * Chamado 2026-0057: os filtros do cabeçalho (seletor e período) têm largura própria de ~210px e
 * ~350px, e era ELA que definia a largura das colunas — a tabela passava de 2.400px numa tela de
 * 1.400. Aqui o filtro ocupa a coluna, e quem define a largura é o minWidth de cada coluna.
 */
.payment-status-table :deep(.p-datatable-thead .p-column-filter),
.payment-status-table :deep(.p-datatable-thead .p-select),
.payment-status-table :deep(.p-datatable-thead .p-datepicker),
.payment-status-table :deep(.p-datatable-thead .p-inputtext) {
  min-width: 0;
  width: 100%;
}

.payment-status-table :deep(.p-datatable-thead .p-select-label) {
  /* O texto do seletor não segura a largura da coluna: encolhe e corta com reticências. */
  flex: 1 1 0;
  width: 0;
  min-width: 0;
  padding-inline: 0.4rem 0;
}

/* A seta do seletor mais estreita devolve espaço ao texto escolhido ("Parcial", "Pendente"). */
.payment-status-table :deep(.p-datatable-thead .p-select-dropdown) {
  width: 1.75rem;
}

/*
 * As linhas quebram em vez de alargar a coluna: nome de cliente, os três valores e os botões de
 * ação passam para a linha de baixo quando não cabem. A linha fica mais alta; a tabela, na tela.
 */
.payment-status-table :deep(.p-datatable .p-datatable-tbody > tr > td) {
  white-space: normal;
}

.payment-status-table :deep(.p-datatable-tbody .action-buttons-cell) {
  flex-wrap: wrap;
  gap: 0.25rem;
}

/* A caixa do filtro parte de zero e cresce até a coluna: um campo de texto tem ~170px próprios. */
.payment-status-table :deep(.p-datatable-thead .p-datatable-filter-element-container) {
  flex: 1 1 0;
  width: 0;
  min-width: 0;
}

/* Título de coluna em duas linhas quando não cabe ("Status Compromisso", "Forma de Pagamento"). */
.payment-status-table :deep(.p-datatable-thead .p-datatable-column-title) {
  white-space: normal;
  line-height: 1.2;
}

/* Ao lado de um seletor o botão de modo de comparação não tem o que oferecer, e custa 40px. */
.payment-status-table :deep(.p-datatable-filter:has(.p-select) .p-datatable-column-filter-button) {
  display: none;
}

/* Header Actions */
.table-header-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

/* Bulk Action Toolbar */
.bulk-action-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 1px solid #86efac;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(34, 197, 94, 0.1);
}

.bulk-selection-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  color: #166534;
}

.bulk-selection-info i {
  font-size: 1.25rem;
  color: #22c55e;
}

.bulk-selection-amount {
  font-weight: 600;
  color: #15803d;
}

.bulk-actions {
  display: flex;
  gap: 0.5rem;
}

/* Disable checkbox for "Pago" rows */
:deep(.row-not-selectable) {
  .p-checkbox {
    pointer-events: none;
    opacity: 0.4;
  }
}

/* Muted text for empty values */
:deep(.text-muted) {
  color: var(--text-color-secondary, #6c757d);
  font-style: italic;
}

/* WhatsApp button styling */
:deep(.btn-whatsapp) {
  white-space: nowrap;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  /* Compacto: o número de 13 dígitos com o respiro padrão do botão pedia 160px de coluna. */
  padding: 0.25rem 0.4rem;
  font-size: 0.8rem;
}

/* Total / Faturado / Líquido column styling */
:deep(.valores-column) {
  display: inline-flex;
  /* Os três valores passam para a linha de baixo quando a coluna é estreita; cada valor fica inteiro. */
  flex-wrap: wrap;
  align-items: center;
  gap: 0.125rem 0.25rem;
  font-size: 0.875rem;
}

:deep(.valores-column > span) {
  white-space: nowrap;
}

:deep(.valor-total) {
  color: var(--text-color, #333);
  font-weight: 600;
}

:deep(.valor-faturado) {
  color: var(--primary-color, #3B82F6);
  font-weight: 500;
}

:deep(.valor-liquido) {
  color: var(--green-600, #16A34A);
  font-weight: 500;
}

:deep(.valor-separator) {
  color: var(--text-color-secondary, #6c757d);
  font-weight: 300;
}

/* Responsive */
@media (max-width: 768px) {
  .bulk-action-toolbar {
    flex-direction: column;
    gap: 0.75rem;
    text-align: center;
  }

  .bulk-selection-info {
    justify-content: center;
    flex-wrap: wrap;
  }

  .bulk-actions {
    width: 100%;
    justify-content: center;
  }
}
</style>
